<script setup lang="ts">
import {
  ref,
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
  markRaw,
} from 'vue';
import { useRegle } from '@regle/core';
import { provideItpFormContext, useItpFormLogic } from './useItpForm';
import { formMessages } from '../../messages';
import type { OpenAPIV3 } from 'openapi-types';

const props = defineProps<{
  id?: string;
  name: string;
  schema?: string | OpenAPIV3.SchemaObject;
  autocomplete?: string;
  autofocus?: string | boolean;
  disabled?: boolean;
  readonly?: boolean;
  row?: boolean;
  labelAlign?: string;
  rules?: Record<string, any>;
  initialValues?: Record<string, any>;
}>();

const formId = computed(() => props.id || `form-${props.name}`);

const emit = defineEmits(['submit']);

const formState = ref<Record<string, any>>({});

const dynamicRules = ref<Record<string, any>>({});

const mergedRules = computed(() => ({
  ...(props.rules || {}),
  ...dynamicRules.value,
}));

const { r$ } = useRegle(formState, mergedRules, {});

const {
  submittable,
  reset,
  submit: onSubmit,
  handleSubmit,
} = useItpFormLogic(r$, formState, props, emit, mergedRules);

function registerField(
  name: string,
  rules: Record<string, any>,
  initialValue?: any,
) {
  if (initialValue !== undefined || !(name in formState.value)) {
    formState.value[name] = sanitizeForReactivity(initialValue ?? null);
  }
  dynamicRules.value[name] = rules;
}

function unregisterField(name: string) {
  delete formState.value[name];
  delete dynamicRules.value[name];
}

function sanitizeForReactivity(value: any): any {
  if (value instanceof File || value instanceof Blob) {
    return markRaw(value);
  }
  if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
    return value.map((f) => (f instanceof File ? markRaw(f) : f));
  }
  return value;
}

function updateFieldValue(name: string, value: any) {
  formState.value[name] = sanitizeForReactivity(value);
}

function isRequired(field: string): boolean {
  const fieldRules = mergedRules.value[field];
  if (!fieldRules || typeof fieldRules !== 'object') {
    return false;
  }
  return !!(fieldRules as any).required || !!(fieldRules as any).accepted;
}

function errorsOf(field: string): string[] {
  const fieldRules = mergedRules.value[field];
  if (fieldRules && typeof fieldRules === 'object') {
    const isReq =
      !!(fieldRules as any).required || !!(fieldRules as any).accepted;
    if (isReq) {
      const val = formState.value[field];
      if (val === null || val === undefined || val === '') {
        const fieldStatus = (r$ as any)?.$fields?.[field];
        if (fieldStatus?.$dirty) {
          return [formMessages.required];
        }
      }
    }
  }
  return [];
}

function touchField(field: string) {
  const fieldStatus = (r$ as any)?.$fields?.[field];
  if (fieldStatus && typeof fieldStatus.$touch === 'function') {
    fieldStatus.$touch();
  }
}

const parentRegisterForm = inject<Function>('itp-register-form', () => {});
const parentUnregisterForm = inject<Function>('itp-unregister-form', () => {});

const validate = async () => {
  const { valid } = await r$.$validate();
  return valid;
};

const hasRequiredField = computed(() =>
  Object.values(mergedRules.value || {}).some(
    (item: any) =>
      item && typeof item === 'object' && (item.required || item.accepted),
  ),
);

const flags = computed(() => {
  const dirty = !!r$.$anyDirty;
  const error = !!r$.$error;
  return {
    untouched: !dirty,
    touched: dirty,
    dirty,
    pristine: !dirty,
    valid: !error,
    invalid: error,
    passed: !!r$.$correct,
    failed: error,
    validated: dirty,
    pending: !!r$.$pending,
    required: hasRequiredField.value,
    changed: !!r$.$anyEdited,
  };
});

const formInstance = {
  id: formId.value,
  name: props.name,
  flags,
  submittable,
  disabled: computed(() => !!props.disabled),
  readonly: computed(() => !!props.readonly),
  handleSubmit,
  validate,
  reset,
};

onMounted(() => {
  parentRegisterForm(props.name, formInstance);
});

onBeforeUnmount(() => {
  parentUnregisterForm(formInstance);
});

defineExpose({
  id: formId.value,
  validate: async () => {
    const { valid } = await r$.$validate();
    return valid;
  },
  reset,
  flags,
  submittable,
  handleSubmit,
  submit: onSubmit,
  r$,
  formState,
  disabled: computed(() => props.disabled),
  readonly: computed(() => props.readonly),
});

provideItpFormContext({
  id: formId.value,
  r$,
  state: formState,
  rules: mergedRules,
  disabled: computed(() => !!props.disabled),
  readonly: computed(() => !!props.readonly),
  labelAlign: computed(() =>
    props.labelAlign === 'right' || props.labelAlign === 'left'
      ? (props.labelAlign as 'left' | 'right')
      : undefined,
  ),
  errorsOf,
  isRequired,
  touchField,
  registerField,
  unregisterField,
  updateFieldValue,
});
</script>

<template>
  <form
    :id="formId"
    :name="name"
    :autocomplete="autocomplete"
    novalidate
    class="itp-form"
    @submit.prevent="onSubmit"
  >
    <slot :form-state="formState" :r$="r$" />
  </form>
</template>
